import type { Option } from '@/legacy/types';

export type DelegationSubtitle =
	| { kind: 'none' }
	| { kind: 'all' }
	| { kind: 'name'; name: string }
	| { kind: 'count'; count: number };

type ResolveDelegationSubtitleParams = {
	/** isSuperUser || hasDelegationAccess — whether the delegations query is even fired. */
	canSeeDelegations: boolean;
	delegationsOptions: Pick<Option, 'value' | 'label'>[] | undefined;
	delegationsIds: number[] | null | undefined;
};

/**
 * Resolves the sidebar card's second line. Returns a discriminated union, not a translated
 * string — the component owns translation so this stays pure/testable.
 *
 * Hard rule: an empty, already-resolved delegations list on the company never renders "all
 * delegations" — that would misleadingly imply delegation scoping exists when it doesn't.
 */
export const resolveDelegationSubtitle = ({
	canSeeDelegations,
	delegationsOptions,
	delegationsIds,
}: ResolveDelegationSubtitleParams): DelegationSubtitle => {
	const scopedCount = delegationsIds && delegationsIds.length > 0 ? delegationsIds.length : 0;

	// No permission to browse delegation options, so delegationsOptions never resolves — the only
	// signal available is the session's own delegationsIds count. Enough for "N selected", not
	// enough to resolve a name or claim "all" (that needs the actual options list).
	if (!canSeeDelegations)
		return scopedCount > 0 ? { kind: 'count', count: scopedCount } : { kind: 'none' };

	// Options still loading: same fallback as above.
	if (delegationsOptions === undefined) {
		return scopedCount > 0 ? { kind: 'count', count: scopedCount } : { kind: 'none' };
	}

	// Resolved and empty: usually the company has no delegations at all, but a session already
	// scoped into one or more delegations (e.g. a multi-company user who switched into a
	// delegation) can hit this too — the options list this call resolves doesn't necessarily
	// cover delegations outside the active scope. delegationsIds is still the source of truth
	// for "am I inside a delegation", so a nonzero scopedCount still gets a count, never 'none'.
	if (delegationsOptions.length === 0) {
		return scopedCount > 0 ? { kind: 'count', count: scopedCount } : { kind: 'none' };
	}

	if (scopedCount === 0) return { kind: 'all' };

	if (scopedCount === 1) {
		const singleId = delegationsIds![0];
		const match =
			singleId != null ? delegationsOptions.find((d) => d.value === singleId) : undefined;
		return match ? { kind: 'name', name: match.label } : { kind: 'count', count: 1 };
	}

	return { kind: 'count', count: scopedCount };
};
